import { ReactElement, useEffect, useRef } from "react";
import { useIBANList } from "./useIBANList";
import { useActiveIBAN } from "./useActiveIBAN";
import { useIBANOperations } from "./useIBANOperations";
import { useIBANModal } from "./useIBANModal";

export const useIBANManagement = () => {
  const hasInitialized = useRef(false);

  const {
    approvedIBANList,
    pendingIBANList,
    rejectedIBANList,
    getIbansState,
    handleSettlementSelection,
    contextHolder: listContextHolder,
  } = useIBANList();

  const { activeIBAN, getActiveIBAN, updateActiveIBAN, contextHolder: activeContextHolder } = useActiveIBAN();

  const {
    showAddIBANModal,
    setShowAddIBANModal,
    addIBANForm,
    handleModalClose,
    handleEditIBAN: handleEditIBANModal,
  } = useIBANModal();

  const {
    handleAddIBAN,
    handleRemoveIBAN,
    retryLastRejectedRequest,
    loading: operationsLoading,
    retryRequestLoading,
    contextHolder: operationsContextHolder,
  } = useIBANOperations({
    approvedIBANList,
    pendingIBANList,
    rejectedIBANList,
    activeIBAN,
    onRefresh: () => getIbansState.retry(),
  });

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      getIbansState.retry();
    }
  }, [getIbansState]);

  const handleAddIBANWithModal = async (
    values: { title: string; iban: string } | { ibans: { title: string; iban: string }[] },
  ) => {
    const success = await handleAddIBAN(values);
    if (success) {
      setShowAddIBANModal(false);
      addIBANForm.resetFields();
    }
  };

  const handleEditIBAN = (ibanId: string) => {
    const iban =
      approvedIBANList.find((item) => item.id === ibanId) ||
      pendingIBANList.find((item) => item.id === ibanId) ||
      rejectedIBANList.find((item) => item.id === ibanId);
    if (iban) {
      handleEditIBANModal(iban);
    }
  };

  // Combine all context holders - return as array to be rendered in component
  const contextHolder = [listContextHolder, activeContextHolder, operationsContextHolder] as ReactElement[];

  return {
    approvedIBANList,
    pendingIBANList,
    rejectedIBANList,
    activeIBAN,
    showAddIBANModal,
    addIBANForm,
    setShowAddIBANModal,
    handleSettlementSelection,
    handleAddIBAN: handleAddIBANWithModal,
    handleEditIBAN,
    handleRemoveIBAN,
    handleModalClose,
    getActiveIBAN,
    updateActiveIBAN,
    contextHolder,
    retryLastRejectedRequest,
    loading: getIbansState.loading || operationsLoading,
    error: getIbansState.error,
    retry: getIbansState.retry,
    retryRequestLoading,
  };
};
